<template>
  <div>
    <v-menu
      v-if="!disabled"
      ref="menu"
      v-model="menu"
      :close-on-content-click="false"
      transition="slide-x-transition"
      :disabled="readonly"
      :nudge-right="nudgeRight"
      :nudge-left="nudgeLeft"
      offset-overflow
      max-width="290px"
      min-width="auto"
      bottom
      offset-y
      :left="left"
    >
      <template #activator="{ on, attrs }">
        <v-text-field
          v-model="dateFormatted"
          :label="label"
          :filled="filled"
          :readonly="readonly"
          :clearable="clearable"
          :class="classList"
          :rules="dateRules"
          :error="error"
          :placeholder="datePickerFormat"
          :autofocus="autofocus"
          :hide-details="hideDetails"
          :flat="flat"
          :dense="dense"
          :outlined="outlined"
          @click:clear="date = ''"
          @blur="date = getISODate(dateFormatted)"
        >
          <template #append>
            <v-icon v-bind="attrs" v-on="on">mdi-calendar</v-icon>
          </template>
        </v-text-field>
      </template>
      <v-date-picker
        :value="pickerDate"
        no-title
        show-current
        :min="minIsoDate"
        :max="maxIsoDate"
        @input="date = $event"
        @change="menu = false"
      />
    </v-menu>
    <div v-else>
      <v-text-field
        v-model="dateFormatted"
        :label="label"
        :filled="filled"
        :rules="dateRules"
        :error="error"
        disabled
        readonly
        :hint="disabledMessage"
        :class="classList"
        :outlined="outlined"
        :clearable="clearable"
        :flat="flat"
        :dense="dense"
        :hide-details="hideDetails"
      >
        <template v-if="disabledMessage" #append>
          <AppTooltip :text="disabledMessage" />
        </template>
      </v-text-field>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { DateTime } from 'luxon';
import { mapState } from 'vuex';

import { Nullable } from '@/utils/types';

import AppTooltip from '@/bundles/App/components/common/AppTooltip.vue';

interface ICommonDatePicker {
  date: Nullable<string>;
  dateFormatted: Nullable<string>;
  menu: boolean;
  isFirstLoad: boolean;
}

const momentToLuxonFormatMapper = {
  'mm/dd/yyyy': 'LL/dd/yyyy',
  'dd/mm/yyyy': 'dd/LL/yyyy',
  'd/m/yyyy': 'd/L/yyyy',
  'm/d/yyyy': 'L/d/yyyy',
}

export default Vue.extend({
  name: 'CommonDatePicker',

  components: { AppTooltip },

  props: {
    readonly: Boolean,
    enterDate: {
      type: [String, Number, Date],
      default: ''
    },
    label: {
      type: String,
      default: 'Date'
    },
    disabled: Boolean,
    disabledMessage: {
      type: String,
      default: ''
    },
    filled: Boolean,
    outlined: Boolean,
    flat: Boolean,
    dense: Boolean,
    clearable: Boolean,
    classList: {
      type: String,
      default: ''
    },
    nudgeRight: {
      type: Number,
      default: 0
    },
    nudgeLeft: {
      type: Number,
      default: 0
    },
    rules: {
      type: Array as PropType<Function[]>,
      default: () => []
    },
    error: Boolean,
    hideDetails: {
      type: [Boolean, String],
      default: 'auto'
    },
    autofocus: Boolean,
    left: Boolean,
    minIsoDate: {
      type: String,
      default: ''
    },
    maxIsoDate: {
      type: String,
      default: ''
    },
  },

  data: () : ICommonDatePicker => ({
    date: '',
    dateFormatted: null,
    menu: false,
    isFirstLoad: true,
  }),

  computed: {
    ...mapState({
      datePickerFormat: (state: any) => state.accountSettings.date_picker_format,
    }),
    // v-date-picker cannot resolve a month from a non-ISO value (e.g. an invalid
    // "2022-Q3" left in `date`), which blanks the calendar. Feed it a valid ISO
    // date or nothing so it falls back to the current month.
    pickerDate (): string {
      return DateTime.fromISO(this.date ?? '').isValid ? (this.date as string) : '';
    },
    dateRules (): Function[] {
      return [
        ...this.rules,
        (value: string) => {
          if (!value) return true;

          if (!this.checkValidDate(value).isValid) {
            return 'Invalid date';
          }

          const isDateLessThanMin = DateTime.fromISO(this.minIsoDate).toMillis() > DateTime.fromISO(this.getISODate(value)).toMillis();
          if (this.minIsoDate && isDateLessThanMin) {
            return 'Min date is ' + this.formatDate(this.minIsoDate);
          }

          const isDateGreaterThanMax = DateTime.fromISO(this.maxIsoDate).toMillis() < DateTime.fromISO(this.getISODate(value)).toMillis();
          if (this.maxIsoDate && isDateGreaterThanMax) {
            return 'Max date is ' + this.formatDate(this.maxIsoDate);
          }

          return true;
        }
      ]
    },
  },

  watch: {
    date: function (val: Nullable<string>) {
      this.dateFormatted = this.formatDate(val);
      this.$emit('updateDate', val);
    },
    enterDate: {
      handler: function (val: Nullable<string | number | Date>) {
        if (this.isFirstLoad && !val) {
          this.isFirstLoad = false;
          return;
        }

        if (typeof val === 'number') {
          val = DateTime.fromMillis(val).toFormat('yyyy-LL-dd');
        } else if (val instanceof Date) {
          val = DateTime.fromJSDate(val).toFormat('yyyy-LL-dd');
        }

        this.date = (val as string);
      },
      immediate: true,
    }
  },

  methods: {
    formatDate (date): string | null {
      if (!date) return null;
      return this.$options.filters?.formatDate(date);
    },
    getISODate (date: Nullable<string>): string {
      if (!date) { return '' }

      return this.checkValidDate(date).date;
    },
    checkValidDate (date: string) {
      const format = momentToLuxonFormatMapper[this.datePickerFormat.toLowerCase()] || 'LL/dd/yyyy';

      const [input1, input2, input3] = date.split('/');
      const paddedDate = `${input1?.padStart(2, '0')}/${input2?.padStart(2, '0')}/${input3?.padStart(4, '20')}`;

      const dateToCheck = DateTime.fromFormat(paddedDate, format);

      if (!dateToCheck.isValid) {
        return {
          date: this.date as string,
          isValid: false,
        };
      }

      return {
        date: dateToCheck.toISODate(),
        isValid: true,
      };
    }
  },
});
</script>
